import { useEffect, useMemo, useRef, type RefObject } from 'react';

import type { MentionSearchType } from '../../types/mentions.service.type';
import type {
  MentionCategoryDescriptor,
  MentionPickerItem,
} from 'features/chat/utils/normalize-mention-search';
import { MentionCategoriesMenu } from './mention-categories-menu';
import { MentionCategoryItemsMenu } from './mention-category-items-menu';
import type { MentionPickerView } from './mention-picker.constants';

export type { MentionPickerView } from './mention-picker.constants';

type ContextMentionPickerProps = {
  open: boolean;
  view: MentionPickerView;
  onViewChange: (view: MentionPickerView) => void;
  category: MentionSearchType | null;
  onCategoryChange: (category: MentionSearchType | null) => void;
  categories: MentionCategoryDescriptor[];
  items: MentionPickerItem[];
  isLoading: boolean;
  isCategoriesLoading?: boolean;
  query: string;
  onSelect: (item: MentionPickerItem) => void;
  onClearFilter?: () => void;
  onClose: () => void;
  inputRef?: RefObject<HTMLElement | null>;
};

export function ContextMentionPicker({
  open,
  view,
  onViewChange,
  category,
  onCategoryChange,
  categories,
  items,
  isLoading,
  isCategoriesLoading,
  query,
  onSelect,
  onClearFilter,
  onClose,
  inputRef,
}: ContextMentionPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);
  const hasSearchQuery = query.trim().length > 0;

  const categoryItems = useMemo(() => {
    if (!category) return items;
    return items.filter((item) => item.type === category);
  }, [items, category]);

  // Typing after / skips categories and shows API search results directly.
  useEffect(() => {
    if (!open) return;
    if (hasSearchQuery) {
      onViewChange('items');
      onCategoryChange(null);
      return;
    }
    if (!category && view === 'items') {
      onViewChange('categories');
    }
  }, [hasSearchQuery, open, category, view, onViewChange, onCategoryChange]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      if (pickerRef.current?.contains(target)) return;
      if (inputRef?.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, onClose, inputRef]);

  const handleCategorySelect = (type: MentionSearchType) => {
    onCategoryChange(type);
    onViewChange('items');
  };

  const handleClearFilter = () => {
    if (hasSearchQuery) {
      onClearFilter?.();
    }

    onCategoryChange(null);
    onViewChange('categories');
  };

  if (!open) return null;

  const showSearchResults = hasSearchQuery;
  const showCategoryItems = !showSearchResults && view === 'items' && category;
  const showCategories = !showSearchResults && view === 'categories';

  return (
    <div
      ref={pickerRef}
      className="absolute bottom-full left-0 right-0 z-[100] flex w-full min-w-0 items-stretch"
      onMouseDown={(event) => event.preventDefault()}
    >
      {showSearchResults ? (
        <MentionCategoryItemsMenu
          items={items}
          isLoading={isLoading}
          query={query}
          onSelect={onSelect}
          onClearFilter={handleClearFilter}
        />
      ) : null}
      {showCategoryItems ? (
        <MentionCategoryItemsMenu
          category={category}
          items={categoryItems}
          isLoading={isLoading}
          query={query}
          onSelect={onSelect}
          onClearFilter={handleClearFilter}
        />
      ) : null}
      {showCategories ? (
        <MentionCategoriesMenu
          categories={categories}
          isLoading={isCategoriesLoading ?? isLoading}
          onSelectCategory={handleCategorySelect}
        />
      ) : null}
    </div>
  );
}
